import * as React from "react";
import { useState } from "react";

import { DropDownList } from "@progress/kendo-react-dropdowns";
import { DatePicker, DatePickerChangeEvent } from "@progress/kendo-react-dateinputs";

import { getEndOfDayDate } from "./date";

export enum DateRangePeriod {
    ThisCycle = "ThisCycle",
    LastCycle = "LastCycle",
    All = "All",
    Custom = "Custom",
    Today = "Today",
    Yesterday = "Yesterday",
    ThisWeek = "ThisWeek",
    ThisMonth = "ThisMonth",
    ThisQuarter = "ThisQuarter",
    ThisYear = "ThisYear",
    LastWeek = "LastWeek",
    LastMonth = "LastMonth",
    LastQuarter = "LastQuarter",
    LastYear = "LastYear",
    PastTwentyFourHours = "PastTwentyFourHours",
    PastFourtyEightHours = "PastFourtyEightHours",
    PastSeventyTwoHours = "PastSeventyTwoHours",
    PastSevenDays = "PastSevenDays",
    PastThirtyDays = "PastThirtyDays",
    PastSixtyDays = "PastSixtyDays",
    PastNinetyDays = "PastNinetyDays"
}

export interface DateRange {
    period: DateRangePeriod,
    customStartDate?: Date | null,
    customEndDate?: Date | null
}

export interface DateRangeOption {
    value: DateRangePeriod,
    text: string
}

const dateRangeOptions: DateRangeOption[] = [
    {
        value: DateRangePeriod.All,
        text: "All Dates"
    },
    {
        value: DateRangePeriod.Custom,
        text: "Choose Dates..."
    },
    {
        value: DateRangePeriod.Today,
        text: "Today"
    },
    {
        value: DateRangePeriod.Yesterday,
        text: "Yesterday"
    },
    {
        value: DateRangePeriod.ThisWeek,
        text: "This Week"
    },
    {
        value: DateRangePeriod.ThisMonth,
        text: "This Month"
    },
    {
        value: DateRangePeriod.ThisQuarter,
        text: "This Quarter"
    },
    {
        value: DateRangePeriod.ThisYear,
        text: "This Year"
    },
    {
        value: DateRangePeriod.ThisCycle,
        text: "This Cycle"
    },
    {
        value: DateRangePeriod.LastWeek,
        text: "Last Week"
    },
    {
        value: DateRangePeriod.LastMonth,
        text: "Last Month"
    },
    {
        value: DateRangePeriod.LastQuarter,
        text: "Last Quarter"
    },
    {
        value: DateRangePeriod.LastYear,
        text: "Last Year"
    },
    {
        value: DateRangePeriod.LastCycle,
        text: "Last Cycle"
    },
    {
        value: DateRangePeriod.PastTwentyFourHours,
        text: "Past 24 Hours"
    },
    {
        value: DateRangePeriod.PastFourtyEightHours,
        text: "Past 48 Hours"
    },
    {
        value: DateRangePeriod.PastSeventyTwoHours,
        text: "Past 72 Hours"
    },
    {
        value: DateRangePeriod.PastSevenDays,
        text: "Past 7 Days"
    },
    {
        value: DateRangePeriod.PastThirtyDays,
        text: "Past 30 Days"
    },
    {
        value: DateRangePeriod.PastSixtyDays,
        text: "Past 60 Days"
    },
    {
        value: DateRangePeriod.PastNinetyDays,
        text: "Past 90 Days"
    }
];

export interface DateRangeSelectProps {
    onChange: (dateRange: DateRange) => void,
    dateRange?: DateRange,
    useFilterButton?: boolean
}

export const DateRangeSelect = ({ onChange, dateRange, useFilterButton }: DateRangeSelectProps) => {
    useFilterButton = useFilterButton ?? true;
    const defaultDateRange = dateRange || { period: DateRangePeriod.All };

    const defaultDateRangeIndex = dateRangeOptions.findIndex(d => d.value == defaultDateRange.period);
    const [selected, setSelected] = useState({ value: dateRangeOptions[defaultDateRangeIndex] });

    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    const handleChange = (event: any) => {
        const selectedDateRangeOption: DateRangeOption = event.target.value;

        const newValue = {
            value: selectedDateRangeOption
        };

        if (newValue.value.value == selected.value.value) {
            return;
        }

        setSelected(newValue);

        if (selectedDateRangeOption.value != DateRangePeriod.Custom) {
            onChange({ period: selectedDateRangeOption.value });
        }
    };

    const applyCustomDateRange = () => {
        if (endDate) {
            onChange({
                period: DateRangePeriod.Custom,
                customStartDate: startDate,
                customEndDate: getEndOfDayDate(endDate)
            });
        }
    }

    const handleSetStartDate = (e: DatePickerChangeEvent) => {
        setStartDate(e.value);
        if(!useFilterButton && endDate) {
            onChange({
                period: DateRangePeriod.Custom,
                customStartDate: e.value,
                customEndDate: endDate
            });
        }
    }

    const handleSetEndDate = (e: DatePickerChangeEvent) => {
        setEndDate(e.value);
        if(!useFilterButton && startDate) {
            onChange({
                period: DateRangePeriod.Custom,
                customStartDate: startDate,
                customEndDate: e.value
            });
        }
    };


    return (<div className="date-select">
        <DropDownList
            data={dateRangeOptions}
            textField="text"
            dataItemKey="value"
            value={selected.value}
            onChange={handleChange}
        />
        {
            selected.value.value == DateRangePeriod.Custom &&
            <div>
                <div>Start Date: <DatePicker value={startDate} onChange={(e: DatePickerChangeEvent) => handleSetStartDate(e)} /></div>
                <div>End Date: <DatePicker value={endDate} onChange={(e: DatePickerChangeEvent) => handleSetEndDate(e)} /></div>
                { useFilterButton && <button type="button" className="k-button" disabled={!startDate || !endDate} onClick={applyCustomDateRange}>Filter</button> }
            </div>
        }
    </div>);
}